import { lazy, Suspense, type ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import { Route, Switch, useLocation, Router as WouterRouter } from 'wouter';

import { CartProvider } from '@/contexts/cart-context';
import { CurrencyProvider } from '@/contexts/currency-context';
import { Layout } from '@/components/layout';
import { initializeAnalytics, trackPageView } from '@/lib/analytics';
import ReferralCapture from '@/pages/affiliate-program/ReferralCapture';

const Home = lazy(() => import('@/pages/home'));
const Shop = lazy(() => import('@/pages/shop'));
const ProductDetail = lazy(() => import('@/pages/product'));
const About = lazy(() => import('@/pages/about'));
const Owners = lazy(() => import('@/pages/owners'));
const LocateUs = lazy(() => import('@/pages/locate-us'));
const RemakeTracking = lazy(() => import('@/pages/remake'));
const OwnerRemakes = lazy(() => import('@/pages/owner-remakes'));
const OwnerPriceLeads = lazy(() => import('@/pages/owner-price-leads'));
const OwnerHome = lazy(() => import('@/pages/owner-home'));
const CustomerDashboard = lazy(() => import('@/pages/customer-dashboard'));
const ReviewPage = lazy(() => import('@/pages/review'));
const OwnerReviews = lazy(() => import('@/pages/owner-reviews'));
const OwnerAnalytics = lazy(() => import('@/pages/owner-analytics'));
const OwnerMarketing = lazy(() => import('@/pages/owner-marketing'));
const OwnerTeam = lazy(() => import('@/pages/owner-team'));
const AffiliatePage = lazy(() => import('@/pages/affiliate-program/AffiliatePage'));
const AffiliateHowItWorksPage = lazy(() => import('@/pages/affiliate-program/AffiliateHowItWorksPage'));
const OwnerAffiliatePage = lazy(() => import('@/pages/affiliate-program/OwnerAffiliatePage'));
const TermsAndConditions = lazy(() =>
  import('@/pages/policies').then((pages) => ({
    default: pages.TermsAndConditions,
  })),
);
const PrivacyPolicy = lazy(() =>
  import('@/pages/policies').then((pages) => ({
    default: pages.PrivacyPolicy,
  })),
);
const RefundPolicy = lazy(() =>
  import('@/pages/policies').then((pages) => ({
    default: pages.RefundPolicy,
  })),
);
const CancellationPolicy = lazy(() =>
  import('@/pages/policies').then((pages) => ({
    default: pages.CancellationPolicy,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

function Router() {
  const [location] = useLocation();

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      trackPageView(location);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location]);

  return (
    <Layout>
      <ReferralCapture />
      <RoutedErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/shop" component={Shop} />
            <Route path="/product/:handle" component={ProductDetail} />
            <Route path="/about" component={About} />
            <Route path="/owners" component={Owners} />
            <Route path="/locate-us" component={LocateUs} />
            <Route path="/my-atelier" component={CustomerDashboard} />
            <Route path="/affiliate" component={AffiliatePage} />
            <Route path="/affiliate/how-it-works" component={AffiliateHowItWorksPage} />
            <Route path="/remake" component={RemakeTracking} />
            <Route path="/remake/:reference" component={RemakeTracking} />
            <Route path="/review/:id" component={ReviewPage} />
            <Route path="/policies/terms-and-conditions" component={TermsAndConditions} />
            <Route path="/policies/privacy-policy" component={PrivacyPolicy} />
            <Route path="/policies/refund-policy" component={RefundPolicy} />
            <Route path="/policies/cancellation-policy" component={CancellationPolicy} />
            <Route path="/owner" component={OwnerHome} />
            <Route path="/owner/remakes" component={OwnerRemakes} />
            <Route path="/owner/price-leads" component={OwnerPriceLeads} />
            <Route path="/owner/reviews" component={OwnerReviews} />
            <Route path="/owner/marketing" component={OwnerMarketing} />
            <Route path="/owner/analytics" component={OwnerAnalytics} />
            <Route path="/owner/affiliates" component={OwnerAffiliatePage} />
            <Route path="/owner/team" component={OwnerTeam} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </RoutedErrorBoundary>
    </Layout>
  );
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function RouteLoadingFallback() {
  return (
    <div role="status" aria-label="Loading page" className="mx-auto min-h-[45vh] max-w-7xl px-4 py-16">
      <div aria-hidden="true" className="h-64 animate-pulse bg-muted/30" />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CurrencyProvider>
        <CartProvider>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </CartProvider>
      </CurrencyProvider>
    </QueryClientProvider>
  );
}

export default App;